import * as React from 'react';
import { useModal } from 'hooks/useModal';
import ModalCreateAccountReminderComponent, {
  Props as ModalCreateAccountReminderProps,
} from 'components/modals/ModalCreateAccountReminder';
import ModalLoginComponent, { Props as ModalLoginProps } from 'components/modals/ModalLogin';
import ModalSignupComponent, { Props as ModalSignupProps } from 'components/modals/ModalSignup';

export const useAuthModals = () => {
  const {
    isOpen: isLoginOpen,
    openModal: openLoginModal,
    closeModal: closeLoginModal,
  } = useModal();
  const {
    isOpen: isSignupOpen,
    openModal: openSignupModal,
    closeModal: closeSignupModal,
  } = useModal();
  const {
    isOpen: isCreateAccountReminderOpen,
    openModal: openCreateAccountReminderModal,
    closeModal: closeCreateAccountReminderModal,
  } = useModal();

  const ModalLogin = React.useMemo(() => {
    const LoginWrapper = ({
      ...props
    }: Omit<ModalLoginProps, 'isOpen' | 'handleClose' | 'toggleSignup'> & {
      handleClose?: (value: boolean) => void;
      toggleSignup?: () => void;
    }) => {
      return (
        <ModalLoginComponent
          appear
          {...props}
          handleClose={props.handleClose || (() => closeLoginModal())}
          toggleSignup={
            props.toggleSignup ||
            (() => {
              closeLoginModal();
              openSignupModal();
            })
          }
          isOpen={isLoginOpen}
        />
      );
    };
    return LoginWrapper;
  }, [isLoginOpen]);

  const ModalSignup = React.useMemo(() => {
    const SignupWrapper = ({
      ...props
    }: Omit<ModalSignupProps, 'isOpen' | 'handleClose' | 'toggleLogin'> & {
      handleClose?: (value: boolean) => void;
      toggleLogin?: () => void;
    }) => {
      return (
        <ModalSignupComponent
          appear
          {...props}
          handleClose={props.handleClose || (() => closeSignupModal())}
          toggleLogin={
            props.toggleLogin ||
            (() => {
              closeSignupModal();
              openLoginModal();
            })
          }
          isOpen={isSignupOpen}
        />
      );
    };
    return SignupWrapper;
  }, [isSignupOpen]);

  const ModalCreateAccountReminder = React.useMemo(() => {
    const CreateAccountReminderWrapper = ({
      ...props
    }: Omit<ModalCreateAccountReminderProps, 'isOpen' | 'handleClose'> & {
      handleClose?: (value: boolean) => void;
    }) => {
      return (
        <ModalCreateAccountReminderComponent
          {...props}
          handleClose={props.handleClose || (() => closeCreateAccountReminderModal())}
          isOpen={isCreateAccountReminderOpen}
        />
      );
    };
    return CreateAccountReminderWrapper;
  }, [isCreateAccountReminderOpen]);

  return {
    isLoginOpen,
    openLoginModal,
    closeLoginModal,
    isSignupOpen,
    openSignupModal,
    closeSignupModal,
    isCreateAccountReminderOpen,
    openCreateAccountReminderModal,
    closeCreateAccountReminderModal,
    ModalLogin,
    ModalSignup,
    ModalCreateAccountReminder,
  };
};
